/*
  This JavaScript handles logic for all Custom Mega Menus.
  VERSION 21.2: Made menu trigger matching more robust to handle whitespace/case differences.
*/

// Define the custom element for desktop/tabbed logic
if (!customElements.get('custom-mega-menu')) {
  class CustomMegaMenu extends HTMLElement {
    constructor() {
      super();
      this.megaMenuTriggerText = this.dataset.megaMenuTrigger;
      this.layout = this.dataset.layout;
      this.animationStyle = this.dataset.animationStyle;
      this.header = document.querySelector('header.header');
      this.menuTriggerElement = null;
      this.megaMenu = this.querySelector('.custom-mega-menu');

      // Hide the wrapper initially to prevent flash of unstyled content if JS fails
      this.style.display = 'none';

      if (this.header) {
        this.init();
      } else {
        document.addEventListener('shopify:section:load', this.init.bind(this));
      }
    }

    init() {
      this.header = document.querySelector('header.header');
      if (!this.header || !this.megaMenuTriggerText) return;

      const navLinks = this.header.querySelectorAll('.header__menu-item');
      
      // --- MODIFIED: More robust matching logic ---
      const triggerTextNormalized = this.megaMenuTriggerText.replace(/\s/g, '').toLowerCase();
      this.menuTriggerElement = Array.from(navLinks).find(link => {
        const linkTextNormalized = link.textContent.replace(/\s/g, '').toLowerCase();
        return linkTextNormalized === triggerTextNormalized;
      });
      // --- END MODIFICATION ---

      if (this.menuTriggerElement) {
        // Show the wrapper now that we have a valid trigger
        this.style.display = 'contents';
        this.header.appendChild(this);
        this.hideDefaultSubmenu();
        this.setupDesktopEventListeners();
        if (this.layout === 'tabbed') {
          this.setupTabEventListeners();
        }
      } else {
        console.warn(`Mega Menu trigger not found for: "${this.megaMenuTriggerText}"`);
      }
    }

    hideDefaultSubmenu() {
      const detailsParent = this.menuTriggerElement.closest('details');
      if (detailsParent) {
        const defaultSubmenu = detailsParent.querySelector('.header__submenu');
        if (defaultSubmenu) defaultSubmenu.style.display = 'none';
      }
    }

    setupDesktopEventListeners() {
      const openMenu = () => {
        clearTimeout(this.closeTimeoutId);
        if (!this.megaMenu.classList.contains('is-open')) {
          this.megaMenu.classList.add('is-open');
          this.menuTriggerElement.setAttribute('aria-expanded', 'true');
          const openEvent = new CustomEvent('megamenu:open', {
            bubbles: true,
            detail: { menuId: this.megaMenu.id }
          });
          this.megaMenu.dispatchEvent(openEvent);

          if (this.animationStyle === 'stagger') {
            this.triggerStaggerAnimation();
          }
        }
      };
      const closeMenuWithDelay = () => {
        this.closeTimeoutId = setTimeout(() => {
          if (this.megaMenu.classList.contains('is-open')) {
            this.megaMenu.classList.remove('is-open');
            this.menuTriggerElement.setAttribute('aria-expanded', 'false');
          }
        }, 250);
      };

      const elementToHover = this.menuTriggerElement.closest('details') || this.menuTriggerElement;
      elementToHover.addEventListener('mouseenter', openMenu);
      elementToHover.addEventListener('mouseleave', closeMenuWithDelay);
      this.megaMenu.addEventListener('mouseenter', () => clearTimeout(this.closeTimeoutId));
      this.megaMenu.addEventListener('mouseleave', closeMenuWithDelay);
      this.menuTriggerElement.addEventListener('click', (event) => {
        event.preventDefault();
        this.megaMenu.classList.contains('is-open') ? closeMenuWithDelay() : openMenu();
      });
      this.megaMenu.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') closeMenuWithDelay();
      });
    }

    triggerStaggerAnimation() {
      let elementsToAnimate;
      if (this.layout === 'tabbed') {
        elementsToAnimate = this.megaMenu.querySelectorAll('.custom-mega-menu__tabs, .custom-mega-menu__tab-panels, .custom-mega-menu__promo-container');
      } else {
        elementsToAnimate = this.megaMenu.querySelectorAll('.custom-mega-menu__column, .custom-mega-menu__promo-wrapper');
      }

      elementsToAnimate.forEach((element, index) => {
        if (element) {
          element.style.animationDelay = `${index * 0.07}s`;
        }
      });
    }

    setupTabEventListeners() {
      this.tabs = this.querySelectorAll('.custom-mega-menu__tab-trigger');
      this.panels = this.querySelectorAll('.custom-mega-menu__tab-panel');
      this.promos = this.querySelectorAll('.custom-mega-menu__promo-wrapper[data-promo-id]');
      if (!this.tabs.length) return;

      this.tabs.forEach(tab => {
        tab.addEventListener('mouseenter', () => this.activateTab(tab));
        tab.addEventListener('focus', () => this.activateTab(tab));
      });
      this.activateTab(this.tabs[0]);
    }

    activateTab(activeTab) {
      if (!activeTab) return;
      const activeTabId = activeTab.dataset.tabId;
      const panelId = `panel-${activeTabId}`;

      this.tabs.forEach(tab => tab.classList.toggle('is-active', tab === activeTab));
      this.panels.forEach(panel => panel.classList.toggle('is-active', panel.id === panelId));
      if (this.promos.length > 0) {
          this.promos.forEach(promo => {
              const isActive = promo.dataset.promoId === activeTabId;
              promo.classList.toggle('is-active', isActive);
          });
      }
    }
  }

  customElements.define('custom-mega-menu', CustomMegaMenu);
}

function initializeAllMobileMegaMenus() {
  const mobileNavDrawer = document.querySelector('#menu-drawer');
  if (!mobileNavDrawer) return;

  const allMegaMenus = document.querySelectorAll('custom-mega-menu');
  if (!allMegaMenus.length) return;

  allMegaMenus.forEach(megaMenuInstance => {
    const triggerText = megaMenuInstance.dataset.megaMenuTrigger;
    const mobileMenuContent = megaMenuInstance.querySelector('.mobile-mega-menu');
    const desktopTriggerElement = megaMenuInstance.menuTriggerElement;

    if (!triggerText || !mobileMenuContent || !desktopTriggerElement) {
        return; // Skip this instance if its desktop trigger wasn't found
    }
    
    const triggerInMobileNav = Array.from(mobileNavDrawer.querySelectorAll('a, summary')).find(el => {
        return el.textContent.replace(/\s/g, '').toLowerCase() === triggerText.replace(/\s/g, '').toLowerCase();
    });

    const triggerParentLi = triggerInMobileNav?.closest('li');

    if (triggerParentLi && !triggerParentLi.querySelector('.mobile-mega-menu')) {
      const originalCaretIcon = desktopTriggerElement.closest('details')?.querySelector('.icon-caret');
      const details = document.createElement('details');
      details.classList.add('menu-drawer__menu-item--has-submenu');
      const summary = document.createElement('summary');
      summary.classList.add('menu-drawer__menu-item', 'link');
      summary.innerHTML = `<span>${triggerText}</span>`;
      if (originalCaretIcon) summary.appendChild(originalCaretIcon.cloneNode(true));

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        details.open = !details.open;
      });

      details.append(summary, mobileMenuContent);
      const newLi = document.createElement('li');
      newLi.appendChild(details);

      triggerParentLi.replaceWith(newLi);
      mobileMenuContent.style.display = 'block';
    }
  });
}

document.addEventListener('DOMContentLoaded', initializeAllMobileMegaMenus);